/**
 * Pure helpers: correlate Unreturned Missed Call report rows with CDR.
 *
 * Yeastar's unreturnmisscall list often labels busy/abandon as "No Answer"
 * with 0s ring. CDR v2 `last_status` (incl. ABANDONED) + `disconnected_by`
 * are closer to ground truth. Historical "was the phone registered at that
 * second?" is NOT available from the public API — only current presence.
 */

import {
  normalizePhoneDigits,
  parseCallTimeMs,
  phonesLikelyMatch,
  recordTouchesExtension,
} from './failoverCorrelation';

export type UnreturnedMissLike = {
  id?: string;
  time: string;
  timestamp?: string | number;
  call_from: string;
  call_to: string;
  ring_duration: number;
  /** no_answer | busy | abandoned */
  miss_call_type: string;
  call_to_type?: string;
  src?: string;
  dst?: string;
};

export type CdrEnrichmentLike = {
  id?: string;
  uid?: string;
  new_id?: string;
  time: string;
  timestamp?: number | string;
  call_from: string;
  call_to: string;
  /** v1 disposition or v2 last_status */
  disposition: string;
  talk_duration?: number;
  ring_duration?: number;
  duration?: number;
  call_duration?: number;
  call_type?: string;
  /** legacy hangup reason string, or synthesized from disconnected_by */
  reason?: string;
  /** Extension | External | System (CDR v2) */
  disconnected_by?: string;
  src_trunk?: string;
  dst_trunk?: string;
  did_number?: string;
  dod_number?: string;
  call_flows?: string;
  segments?: number;
};

export type ExtensionPresenceHint = {
  number: string;
  registered?: boolean;
  devices?: string[];
  presence?: string;
};

export type UnreturnedCdrMatch = {
  matched: boolean;
  cdrDisposition?: string;
  cdrReason?: string;
  cdrDisconnectedBy?: string;
  cdrRingSeconds?: number;
  cdrTalkSeconds?: number;
  cdrDurationSeconds?: number;
  cdrCallType?: string;
  srcTrunk?: string;
  didNumber?: string;
  callPath?: string;
  /** Report miss type disagrees with CDR (e.g. report No Answer, CDR ABANDONED) */
  dispositionMismatch: boolean;
  zeroRing: boolean;
  extRegistered?: boolean;
  extDevices?: string;
  extPresence?: string;
  /** Plain-language outcome for operators */
  whatHappened: string;
};

/** Pull extension/queue digits from "Name<2001>" or bare "2001". */
export function extractCalleeNumber(callTo: string | undefined | null): string {
  const raw = String(callTo || '').trim();
  if (!raw) return '';
  const angle = raw.match(/<([^>]+)>/);
  if (angle?.[1]) {
    const digits = normalizePhoneDigits(angle[1]);
    if (digits) return digits;
    return angle[1].trim();
  }
  const digits = normalizePhoneDigits(raw);
  return digits || raw;
}

export function normalizeMissCallType(type: string | undefined | null): string {
  return String(type || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function normalizeCdrDispositionLabel(disposition: string | undefined | null): string {
  const d = String(disposition || '').trim();
  if (!d) return '';
  const key = d.toLowerCase().replace(/[\s_-]+/g, '');
  if (key === 'busy') return 'BUSY';
  if (key === 'abandoned' || key === 'abandon') return 'ABANDONED';
  if (key === 'noanswer' || key === 'noanswered' || key === 'missed') return 'NO ANSWER';
  if (key === 'answered' || key === 'answer') return 'ANSWERED';
  if (key === 'failed' || key === 'fail') return 'FAILED';
  if (key === 'voicemail' || key === 'vm') return 'VOICEMAIL';
  if (key === 'cancel' || key === 'cancelled' || key === 'canceled') return 'CANCELLED';
  return d.toUpperCase();
}

function reportMissBucket(missType: string): 'busy' | 'no_answer' | 'abandoned' | 'other' {
  const t = normalizeMissCallType(missType);
  if (t.includes('busy')) return 'busy';
  if (t.includes('abandon')) return 'abandoned';
  if (t.includes('noanswer') || t.includes('miss') || t === 'noansw') return 'no_answer';
  return 'other';
}

export function cdrMissBucket(
  disposition: string
): 'busy' | 'no_answer' | 'abandoned' | 'answered' | 'failed' | 'voicemail' | 'other' {
  const d = String(disposition || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (d.includes('busy')) return 'busy';
  if (d.includes('voicemail') || d.includes('vm')) return 'voicemail';
  if (d.includes('abandon')) return 'abandoned';
  if (d.includes('answer') && !d.includes('no')) return 'answered';
  if (d.includes('fail') || d.includes('cancel')) return 'failed';
  if (d.includes('noanswer') || d.includes('miss')) return 'no_answer';
  return 'other';
}

function dispositionsMismatch(reportMiss: string, cdrDisposition: string): boolean {
  const report = reportMissBucket(reportMiss);
  const cdr = cdrMissBucket(cdrDisposition);
  if (cdr === 'other' || report === 'other') return false;
  if (cdr === 'answered') return true;
  if (report === 'busy' && cdr === 'busy') return false;
  if (report === 'no_answer' && cdr === 'no_answer') return false;
  if (report === 'abandoned' && cdr === 'abandoned') return false;
  // Report "No Answer" + CDR ABANDONED/BUSY is a common Yeastar summary mismatch
  return report !== cdr;
}

function formatSeconds(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function softReason(reason?: string): string {
  if (!reason) return '';
  return reason
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/caller_call_callee/gi, '→')
    .replace(/callee_call_caller/gi, '←')
    .replace(/\bhangup\b/gi, 'hung up')
    .replace(/\bno[_ ]?answer\b/gi, 'no answer')
    .replace(/\bbusy\b/gi, 'busy');
}

function whoHungUp(disconnectedBy?: string, reason?: string): string {
  const by = String(disconnectedBy || '').toLowerCase();
  if (by === 'external') return 'the external caller';
  if (by === 'extension') return 'the extension / internal party';
  if (by === 'system') return 'the PBX system (timeout / forced release)';
  const r = String(reason || '').toLowerCase();
  if (r.includes('src hang') || r.includes('caller')) return 'the caller';
  if (r.includes('dst hang') || r.includes('callee')) return 'the callee';
  if (r.includes('forward')) return 'a forward / divert path';
  return '';
}

function presenceNote(hint?: ExtensionPresenceHint | null): string {
  if (!hint) return '';
  const bits: string[] = [];
  if (hint.registered === false) {
    bits.push('currently offline (no registered devices)');
  } else if (hint.registered === true) {
    const devs = (hint.devices || []).filter(Boolean);
    bits.push(
      devs.length
        ? `currently registered (${devs.join(', ')})`
        : 'currently registered'
    );
  }
  if (hint.presence && !/^—$|^available$/i.test(hint.presence)) {
    bits.push(`presence: ${hint.presence}`);
  }
  if (!bits.length) return '';
  return `Extension ${hint.number} is ${bits.join('; ')} right now (not historical at call time).`;
}

/**
 * Build operator-facing explanation from report row + optional CDR + presence.
 */
export function explainUnreturnedMiss(
  row: UnreturnedMissLike,
  cdr?: CdrEnrichmentLike | null,
  presence?: ExtensionPresenceHint | null
): string {
  const ringReport = Number(row.ring_duration) || 0;
  const dest = String(row.call_to || 'destination').trim() || 'destination';
  const reportBucket = reportMissBucket(row.miss_call_type);
  const reportLabel =
    reportBucket === 'busy'
      ? 'Busy'
      : reportBucket === 'abandoned'
        ? 'Abandoned'
        : 'No Answer';

  if (!cdr) {
    const base =
      ringReport <= 0
        ? `${reportLabel} to ${dest} with 0s ring — call never entered a measurable ring state (caller hung up immediately, offline device, DND, busy, or instant reject). No matching CDR found for more detail.`
        : `${reportLabel} to ${dest} after ${formatSeconds(ringReport)} ring. No matching CDR found.`;
    const p = presenceNote(presence);
    return p ? `${base} ${p}` : base;
  }

  const cdrLabel = normalizeCdrDispositionLabel(cdr.disposition);
  const cdrBucket = cdrMissBucket(cdr.disposition);
  const cdrRing =
    cdr.ring_duration != null && Number.isFinite(Number(cdr.ring_duration))
      ? Number(cdr.ring_duration)
      : ringReport;
  const totalDur =
    cdr.duration != null && Number.isFinite(Number(cdr.duration))
      ? Number(cdr.duration)
      : cdr.call_duration != null && Number.isFinite(Number(cdr.call_duration))
        ? Number(cdr.call_duration)
        : undefined;
  const reason = softReason(cdr.reason);
  const hungUp = whoHungUp(cdr.disconnected_by, cdr.reason);
  const mismatch = dispositionsMismatch(row.miss_call_type, cdr.disposition);
  const zeroRing = cdrRing <= 0 && ringReport <= 0;

  const parts: string[] = [];

  if (cdrBucket === 'abandoned') {
    if (zeroRing) {
      parts.push(
        `CDR shows ABANDONED to ${dest} with 0s ring — the call ended before the phone rang (or before ring time was recorded).`
      );
      if (hungUp) {
        parts.push(`Ended by ${hungUp}.`);
      } else {
        parts.push(
          'Usually the external caller hung up very quickly, or the call never reached a ringing handset.'
        );
      }
      if (totalDur != null) {
        parts.push(`Total call duration was ${formatSeconds(totalDur)}.`);
      }
      parts.push(
        'This is not a classic “rang and nobody picked up” miss — the handset never had a measurable ring.'
      );
    } else {
      parts.push(
        `CDR shows ABANDONED to ${dest} after ${formatSeconds(cdrRing)} of ring/routing.`
      );
      if (hungUp) parts.push(`Ended by ${hungUp}.`);
      else parts.push('Caller likely hung up while the destination was being offered.');
    }
  } else if (cdrBucket === 'busy') {
    parts.push(
      `CDR shows BUSY for ${dest} — extension was already on a call or rejected as busy.`
    );
    if (zeroRing) parts.push('Phone never rang (0s ring).');
  } else if (cdrBucket === 'failed') {
    parts.push(
      `CDR shows ${cdrLabel || 'FAILED'} for ${dest} — call did not complete to a ringing handset.`
    );
    if (zeroRing) {
      parts.push('0s ring suggests offline device, DND, or instant SIP reject.');
    }
  } else if (cdrBucket === 'voicemail') {
    parts.push(`Call reached voicemail for ${dest} (CDR: VOICEMAIL).`);
  } else if (cdrBucket === 'no_answer') {
    if (zeroRing) {
      parts.push(
        `CDR shows NO ANSWER with 0s ring to ${dest} — handset likely never rang (offline/DND/reject), or the platform recorded no ring time.`
      );
      if (hungUp) parts.push(`Ended by ${hungUp}.`);
      // Yeastar UI sometimes labels these "Call abandoned" while API disposition stays NO ANSWER
      if (hungUp.includes('caller') || hungUp.includes('external')) {
        parts.push(
          'If the Yeastar CDR UI says “Call abandoned”, that usually means the caller hung up — same outcome as abandon, even when the status field says NO ANSWER.'
        );
      }
    } else {
      parts.push(
        `CDR shows NO ANSWER to ${dest} after ${formatSeconds(cdrRing)} ring — phone rang and was not answered.`
      );
      if (hungUp) parts.push(`Ended by ${hungUp}.`);
    }
  } else if (cdrBucket === 'answered') {
    parts.push(
      `CDR shows ANSWERED for ${dest} — unusual for an unreturned miss; verify callback linkage.`
    );
  } else {
    parts.push(`CDR status: ${cdrLabel || 'unknown'} for ${dest}.`);
  }

  if (mismatch && reportBucket === 'no_answer' && cdrBucket === 'abandoned') {
    parts.push(
      'Unreturned report listed this as No Answer; CDR (v2 last_status) says ABANDONED.'
    );
  } else if (mismatch && reportBucket === 'no_answer' && cdrBucket === 'busy') {
    parts.push('Unreturned report listed this as No Answer (Yeastar summary mismatch).');
  } else if (mismatch) {
    parts.push(
      `Unreturned report listed this as ${reportLabel}; CDR says ${cdrLabel || cdrBucket}.`
    );
  }

  if (reason && !parts.some((p) => p.toLowerCase().includes(reason.toLowerCase().slice(0, 12)))) {
    parts.push(`Reason: ${reason}.`);
  }

  if (cdr.call_flows) {
    parts.push(`Call path: ${cdr.call_flows}.`);
  }

  const trunk = String(cdr.src_trunk || '').trim();
  const did = String(cdr.did_number || '').trim();
  if (trunk || did) {
    const bits = [did && `DID ${did}`, trunk && `trunk ${trunk}`].filter(Boolean);
    if (bits.length) parts.push(`Via ${bits.join(', ')}.`);
  }

  // Honest limit: API cannot prove historical registration at call time
  if (zeroRing && (cdrBucket === 'abandoned' || cdrBucket === 'no_answer' || cdrBucket === 'failed')) {
    parts.push(
      'Note: Yeastar API does not store historical “was the phone registered at that second?” — only CDR status/who hung up, plus current registration below if available.'
    );
  }

  const p = presenceNote(presence);
  if (p) parts.push(p);

  return parts.join(' ');
}

function cdrIds(record: CdrEnrichmentLike): string[] {
  return [record.id, record.uid, record.new_id]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
}

function scoreCdrMatch(row: UnreturnedMissLike, cdr: CdrEnrichmentLike): number {
  const rowMs = parseCallTimeMs(row.time, row.timestamp);
  const cdrMs = parseCallTimeMs(cdr.time, cdr.timestamp);
  if (!rowMs || !cdrMs) return -1;

  const timeDiff = Math.abs(rowMs - cdrMs);
  // Allow small clock / report skew (seconds)
  if (timeDiff > 15_000) return -1;

  const callerOk =
    phonesLikelyMatch(row.call_from, cdr.call_from) ||
    phonesLikelyMatch(row.src || '', cdr.call_from) ||
    phonesLikelyMatch(row.call_from, cdr.call_to);
  if (!callerOk) return -1;

  const destNum = extractCalleeNumber(row.call_to || row.dst);
  const destOk =
    !destNum ||
    recordTouchesExtension(
      {
        call_from: cdr.call_from,
        call_to: cdr.call_to,
        time: cdr.time,
        disposition: cdr.disposition,
        talk_duration: cdr.talk_duration || 0,
      },
      destNum
    ) ||
    phonesLikelyMatch(row.call_to, cdr.call_to) ||
    phonesLikelyMatch(row.dst || '', cdr.call_to);
  if (!destOk) return -1;

  let score = 1000 - Math.floor(timeDiff / 100);
  const ctype = String(cdr.call_type || '').toLowerCase();
  if (ctype.includes('inbound')) score += 50;
  if (ctype.includes('internal')) score -= 20;
  if (String(row.time).trim() === String(cdr.time).trim()) score += 100;
  const rid = String(row.id || '').trim();
  if (rid && cdrIds(cdr).includes(rid)) score += 500;
  return score;
}

/**
 * Find best CDR for one unreturned miss row. Marks used indexes when provided.
 */
export function matchCdrForUnreturned(
  row: UnreturnedMissLike,
  cdrs: CdrEnrichmentLike[],
  usedIndexes?: Set<number>
): { cdr: CdrEnrichmentLike | null; index: number } {
  let bestIdx = -1;
  let bestScore = -1;

  for (let i = 0; i < cdrs.length; i++) {
    if (usedIndexes?.has(i)) continue;
    const score = scoreCdrMatch(row, cdrs[i]);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  if (bestIdx < 0) return { cdr: null, index: -1 };
  return { cdr: cdrs[bestIdx], index: bestIdx };
}

export function buildUnreturnedCdrMatch(
  row: UnreturnedMissLike,
  cdr: CdrEnrichmentLike | null,
  presence?: ExtensionPresenceHint | null
): UnreturnedCdrMatch {
  const zeroRing = (Number(row.ring_duration) || 0) <= 0;
  if (!cdr) {
    return {
      matched: false,
      dispositionMismatch: false,
      zeroRing,
      extRegistered: presence?.registered,
      extDevices: presence?.devices?.join(', ') || undefined,
      extPresence: presence?.presence,
      whatHappened: explainUnreturnedMiss(row, null, presence),
    };
  }

  const duration =
    cdr.duration != null
      ? Number(cdr.duration)
      : cdr.call_duration != null
        ? Number(cdr.call_duration)
        : undefined;

  return {
    matched: true,
    cdrDisposition: normalizeCdrDispositionLabel(cdr.disposition) || cdr.disposition,
    cdrReason: String(cdr.reason || '').trim() || undefined,
    cdrDisconnectedBy: String(cdr.disconnected_by || '').trim() || undefined,
    cdrRingSeconds:
      cdr.ring_duration != null && Number.isFinite(Number(cdr.ring_duration))
        ? Number(cdr.ring_duration)
        : undefined,
    cdrTalkSeconds:
      cdr.talk_duration != null && Number.isFinite(Number(cdr.talk_duration))
        ? Number(cdr.talk_duration)
        : undefined,
    cdrDurationSeconds:
      duration != null && Number.isFinite(duration) ? duration : undefined,
    cdrCallType: String(cdr.call_type || '').trim() || undefined,
    srcTrunk: String(cdr.src_trunk || '').trim() || undefined,
    didNumber: String(cdr.did_number || '').trim() || undefined,
    callPath: String(cdr.call_flows || '').trim() || undefined,
    dispositionMismatch: dispositionsMismatch(row.miss_call_type, cdr.disposition),
    zeroRing,
    extRegistered: presence?.registered,
    extDevices: presence?.devices?.join(', ') || undefined,
    extPresence: presence?.presence,
    whatHappened: explainUnreturnedMiss(row, cdr, presence),
  };
}

/**
 * Enrich a list of unreturned rows with best-effort CDR matches (1:1, greedy).
 */
export function enrichUnreturnedRowsWithCdr<T extends UnreturnedMissLike>(
  rows: T[],
  cdrs: CdrEnrichmentLike[],
  presenceByExt?: Map<string, ExtensionPresenceHint>
): Array<T & UnreturnedCdrMatch> {
  const used = new Set<number>();
  return rows.map((row) => {
    const { cdr, index } = matchCdrForUnreturned(row, cdrs, used);
    if (index >= 0) used.add(index);
    const dest = extractCalleeNumber(row.call_to || row.dst);
    const presence = dest ? presenceByExt?.get(dest) : undefined;
    const match = buildUnreturnedCdrMatch(row, cdr, presence);
    return { ...row, ...match };
  });
}
